import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Space,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps, FilterValueProps, mappingFilterFields,
  mappingFilterToQuery, mappingParamsFilter, mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import useDidMount from 'common/hooks/useDidMount';
import { deleteCustomerService, getAllCustomerService } from 'common/services/extends/customer';
import {
  ROUTE_PATHS
} from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';

export interface CustomersData {
  id: number,
  name: string,
  email: string,
  createdAt: string,
  updatedAt: string,
  client: {
    id: number,
    name: string
  }
}

const CustomersManagement: React.FC<ActiveRoles> = ({ roleCreate, roleUpdate, roleDelete }) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigation = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);

  const pageParam = searchParams.get('page');

  /* Selectors */
  const { system: { defaultPageSize, advancedFilter } } = useAppSelector((state) => state);

  /* State */
  const [currentPage, setCurrentPage] = useState(Number(pageParam));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));

  /* Variables */
  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('customer', advancedFilter),
    [advancedFilter]
  );

  const queryKey = ['getAllCustomerService', keyword, currentPage, currentView, selectedFilterList];

  /* Queries */
  const { data: customersData, isLoading } = useQuery(
    queryKey,
    () => getAllCustomerService({
      keyword,
      page: currentPage,
      limit: currentView,
      ...returnParamsFilter,
    }),
    {
      enabled: !!currentPage,
    }
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['deleteCustomerMutate'],
    async (ids: number[]) => deleteCustomerService(ids),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            message.error(error?.message || '');
          });
        } else {
          message.error(t('message.deleteError'));
        }
      },
    }
  );

  const tableData: CustomersData[] = useMemo(() => (
    customersData?.data.map((val) => ({
      id: val.customerData.id,
      name: val.customerData?.name || '',
      email: val.customerData?.primaryEmail || '',
      createdAt: val.customerData?.createdAt || '',
      updatedAt: val.customerData?.updatedAt || '',
      client: {
        id: val.customerData.client?.id || 0,
        name: val.customerData.client?.name || ''
      }
    })) || []), [customersData]);

  const columns: ColumnsType<CustomersData> = [
    {
      title: 'ID',
      key: 'id',
      width: 55,
      align: 'center',
      fixed: 'left',
      render: (_name: string, data: CustomersData) => (
        <Typography.Text>
          {data.id || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('system.name'),
      key: 'name',
      render: (_name: string, data: CustomersData) => (
        <Typography.Text>
          {data.name || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('system.email'),
      key: 'email',
      render: (_name: string, data: CustomersData) => (
        <Typography.Text>
          {data.email || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('Client'),
      key: 'client',
      render: (_name: string, data: CustomersData) => (
        <Typography.Text>
          {data.client?.name || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('system.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_name: string, data: CustomersData) => (
        <Typography.Text>
          {formatDateTime(data.createdAt || '')}
        </Typography.Text>
      ),
    },
    {
      title: t('system.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_name: string, data: CustomersData) => (
        <Typography.Text>
          {formatDateTime(data.updatedAt || '')}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      render: (_name: string, data: CustomersData) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => navigation(`${ROUTE_PATHS.CUSTOMER_DETAIL}?id=${data.id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              disabled={!roleDelete || deleteLoading}
              loading={deleteLoading}
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  className: 't-pagetable_deleteRecordModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => {
                    deleteMutate([data.id]);
                  },
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Change page view
  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: page.toString() }, { replace: true });
  };

  const handleDeleteMultiple = (data: CustomersData[]) => {
    const ids = data.map((item) => item.id);
    if (ids.length > 0) {
      deleteMutate(ids);
    }
  };

  // Change page size
  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
  };

  // Add filter
  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
  };

  /* Effects */
  useEffect(() => {
    setCurrentPage(pageParam ? Number(pageParam) : 1);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList),
      page: pageParam || '1'
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, setSearchParams, selectedFilterList]);

  useDidMount(() => {
    if (pageParam) return setCurrentPage(Number(pageParam));
    return setCurrentPage(Number(pageParam) || 1);
  });

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.customer')}
        rightHeader={(
          <Space>
            <Button
              disabled={!roleCreate}
              type="primary"
              onClick={() => navigation(ROUTE_PATHS.CUSTOMER_DETAIL)}
            >
              <PlusOutlined />
              {t('system.create')}
            </Button>
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          handleDelete={handleDeleteMultiple}
          isLoading={isLoading || deleteLoading}
          handleSearch={setKeyword}
          tableProps={{
            initShowColumns: ['id', 'name', 'email', 'createdAt', 'updatedAt', 'action', 'client'],
            columns,
            pageData: tableData || [],
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: customersData?.meta.total ?? 1,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields,
          }}
          roles={{ roleCreate, roleUpdate, roleDelete }}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter
          }}
        />
      </div>
    </>
  );
};

export default CustomersManagement;
